import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { RoutesPath } from '../routes-path';
import { User } from '../models/user/user.model';
import { UserSettings } from '@shared/models/user/user-settings.model';
import {
  BehaviorSubject,
  catchError,
  defaultIfEmpty,
  firstValueFrom,
  Observable,
  of,
  Subject,
} from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { SocialAuthService } from '@abacritt/angularx-social-login';
import { MCD_SITE_DIRECTORY, McdSiteInfo } from '@shared/mcd-site-directory';
import { isMcdDashboardAccessOverrideEnabled, resolveMcdDashboardAccess } from '@shared/mcd-access';
import { areMcdDiagnosticsEnabled, logMcdDiagnosticsOnce } from '@shared/mcd-diagnostics';
import { UserGroupsService, UserGroupsState } from './user-groups.service';

@Injectable()
export class StorageService {
  static SESSION_COOKIE: string = 'ph-jwt-token';
  static WAS_VISITED: string = '';
  static SESSION_REFRESH_COOKIE: string = 'ph-refresh-jwt-token';
  static USER_COOKIE: string = 'ph-user';
  static MOBILE_APP_BANNER_COOKIE = 'ph-mobile-app-banner';
  static USER_SETTINGS: string = 'ph-user-settings';
  static AUTH_FROM_HELP_UKRAINE: string = 'ph-auth-from-help-ukraine';
  static AUTH_FROM_WCD: string = 'ph-auth-from-wcd';
  static AUTH_FROM_BACKBASE: string = 'ph-auth-from_backbase';
  public storageSub: any = new Subject<any>();
  readonly sessionEnded$ = new Subject<void>();
  public noScrollAfterChangeRoute: boolean = false;

  public _userInfo$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  _userInfo = this._userInfo$.asObservable();

  constructor(
    public router: Router,
    public translate: TranslateService,
    public socialAuthService: SocialAuthService,
    private injector: Injector
  ) {}

  changeUserInfo(ui: boolean) {
    this._userInfo$.next(ui);
  }

  get(key: string) {
    return localStorage.getItem(key);
  }

  getLang() {
    return this.get('lang');
  }

  put(key: string, val: any) {
    if (key === 'autofocus') {
      this.storageSub.next('added');
    }
    localStorage.setItem(key, val);
  }

  delete(key: string) {
    this.storageSub.next('removed');
    localStorage.removeItem(key);
  }

  logout(goToHome: boolean = true) {
    this.sessionEnded$.next();
    this.userGroups.clear();
    this.delete(StorageService.SESSION_COOKIE);
    this.delete(StorageService.SESSION_REFRESH_COOKIE);
    this.delete(StorageService.USER_COOKIE);
    this.delete(StorageService.USER_SETTINGS);
    this.delete(StorageService.AUTH_FROM_HELP_UKRAINE);
    this.delete(StorageService.AUTH_FROM_WCD);
    this.delete(StorageService.MOBILE_APP_BANNER_COOKIE);
    this.socialAuthService
      .signOut()
      .then()
      .catch(() => {});

    this.changeUserInfo(false);

    if (goToHome) {
      this.router.navigate(['/' + RoutesPath.default.home]);
    }
  }

  get user(): User {
    return JSON.parse(this.get(StorageService.USER_COOKIE));
  }

  // The local-only override remains separate from the backend membership check.
  get isMcdEmployee(): boolean {
    const user = this.user;
    const overrideEnabled = isMcdDashboardAccessOverrideEnabled();
    const groupsState = this.userGroups.stateForUser(user?.id);
    const groupsStatus = groupsState?.status ?? 'not-loaded';
    const hasActiveMcdGroup =
      groupsState?.items.some((group) => group.slug === 'mcd' && group.is_active === true) ?? false;
    const hasLoadedMcdGroup = groupsStatus === 'loaded' && hasActiveMcdGroup;
    const resolvedAccess = resolveMcdDashboardAccess(hasLoadedMcdGroup, overrideEnabled);
    const result = !!user && resolvedAccess;
    const groupsError = this.describeGroupsError(groupsState?.error);

    if (areMcdDiagnosticsEnabled()) {
      const path = typeof window !== 'undefined' ? window.location.pathname : null;
      const diagnostics = {
        hasUser: !!user,
        groupsStatus,
        groupsError,
        hasActiveMcdGroup,
        overrideEnabled,
        resolvedAccess,
        isMcdEmployee: result,
        path,
      };
      const stateKey = [
        !!user,
        groupsStatus,
        hasActiveMcdGroup,
        overrideEnabled,
        resolvedAccess,
        result,
        path ?? 'no-path',
      ].join('|');
      logMcdDiagnosticsOnce('StorageService.isMcdEmployee', stateKey, diagnostics);
    }

    return result;
  }

  // The restaurant SITE number is embedded in the employee's own login email
  // (pl-00355@pl.mcd.com -> SITE 355) rather than being a separate profile field.
  get mcdSiteNumber(): number | null {
    const match = this.user?.email?.toLowerCase().match(/^pl-0*(\d+)@pl\.mcd\.com$/);
    return match ? parseInt(match[1], 10) : null;
  }

  // TEMPORARY: see mcd-site-directory.ts — restaurant address/Mall flag will come
  // from the backend once the SITE spreadsheet import exists.
  get mcdSiteInfo(): McdSiteInfo | null {
    const site = this.mcdSiteNumber;
    return site !== null ? MCD_SITE_DIRECTORY[site] ?? null : null;
  }

  get isMcdMallEmployee(): boolean {
    return !!this.mcdSiteInfo?.isMall;
  }

  get userSettings(): UserSettings {
    return JSON.parse(this.get(StorageService.USER_SETTINGS));
  }

  get isAuthFromUkraine(): boolean {
    return JSON.parse(this.get(StorageService.AUTH_FROM_HELP_UKRAINE));
  }

  get isAuthFromWCD(): boolean {
    return JSON.parse(this.get(StorageService.AUTH_FROM_WCD));
  }

  get isAuthFromBackbase(): boolean {
    return JSON.parse(this.get(StorageService.AUTH_FROM_BACKBASE));
  }

  setToken(token) {
    this.put(StorageService.SESSION_COOKIE, token.token);
    this.put(StorageService.SESSION_REFRESH_COOKIE, token.refresh_token);
  }

  setUser(user): Observable<UserGroupsState> {
    this.put(StorageService.USER_COOKIE, JSON.stringify(user));
    return this.userGroups.setUser(user?.id);
  }

  initializeUserGroups(): Promise<void> {
    const userId = this.user?.id;
    if (!this.get(StorageService.SESSION_COOKIE) || userId === null || userId === undefined) {
      this.userGroups.clear();
      return Promise.resolve();
    }
    return firstValueFrom(
      this.userGroups.setUser(userId).pipe(
        defaultIfEmpty({
          userId: null,
          status: 'idle' as const,
          items: [],
          error: null,
        }),
        catchError((error: unknown) => {
          console.error('Failed to initialize user groups.', error);
          return of(this.userGroups.state);
        })
      )
    ).then(() => undefined);
  }

  refreshUserGroups(): void {
    this.userGroups.refreshForUser(this.user?.id);
  }

  get userGroupsState$() {
    return this.userGroups.state$;
  }

  setAuthFromUkraine(auth: boolean) {
    this.put(StorageService.AUTH_FROM_HELP_UKRAINE, auth);
  }

  setAuthFromWcd(auth: boolean) {
    this.put(StorageService.AUTH_FROM_WCD, auth);
  }

  setAuthFromBackbase(auth: boolean) {
    this.put(StorageService.AUTH_FROM_BACKBASE, auth);
  }

  setUserSettings(userSettings) {
    this.put(StorageService.USER_SETTINGS, JSON.stringify(userSettings));
  }

  hideMobileAppBanner() {
    this.put(StorageService.MOBILE_APP_BANNER_COOKIE, 'hidden');
  }

  isMobileAppBannerHidden(): boolean {
    return this.get(StorageService.MOBILE_APP_BANNER_COOKIE) == 'hidden';
  }

  projectOwnership(id): boolean {
    if (this.user) {
      return id === this.user.id;
    }
    return false;
  }

  eventOwnership(id): boolean {
    if (this.user) {
      return id === this.user.id;
    }
    return false;
  }

  setVisit() {
    this.put(StorageService.WAS_VISITED, 'true');
  }

  setAvatarInitials(name: string, surname: string) {
    if (name === null && surname === null) {
      return this.translate.instant('GLOBAL.anonymous');
    }
    return (name ? name.charAt(0) : '') + ' ' + (surname ? surname.charAt(0) : '');
  }

  formatVote(vote: number) {
    if (Number.isInteger(vote)) {
      return vote.toFixed(1);
    }
    return vote;
  }

  private describeGroupsError(error: unknown): string | null {
    if (!error) {
      return null;
    }
    if (error instanceof Error) {
      return error.message;
    }
    return typeof error === 'string' ? error : 'GET /user/groups failed';
  }

  private get userGroups(): UserGroupsService {
    return this.injector.get(UserGroupsService);
  }
}
